/**
 * Client billing mirror — Masco TWA / Play model (2026-08-22-K).
 *
 * Free: 10 000 credits start, +10 000 each ISO week; weekly four;
 *       switching the four costs exactly 10 000.
 * Subscriber ($4.99/mo): first 14 days free; 50 000 each ISO week (5 swaps at 10 000).
 * VIP ($9.99/mo): first 14 days free; unlimited credits + unlimited swaps.
 *
 * Real charges: Google Play Billing only. No Stripe / web checkout.
 * Closed-test activate* methods remain for QA demos of the local math.
 */

import { apiJson } from "../api/client";
import { isServerMode } from "../api/backend";
import { en } from "../i18n/en";
// Safe direction: weights.ts holds constants only and never imports billing,
// so this does not recreate the billing <-> matching cycle the weekKey helper
// below was duplicated to avoid.
import { heartsPerWeek } from "../matching/weights";

/** ISO week key — duplicated lightly to avoid billing ↔ matching import cycles. */
function weekKey(d: Date = new Date()): string {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

export const TRIAL_DAYS = 14;
export const SUBSCRIPTION_PRICE_USD = 4.99;
export const VIP_PRICE_USD = 9.99;

export const FREE_START_CREDITS = 10_000;
export const FREE_WEEKLY_RECHARGE = 10_000;
export const WEEKLY_SWITCH_COST = 10_000;
export const SUBSCRIBER_START_CREDITS = 50_000;
/** Subscriber refresh of the four — same 10 000 unit; 50 000 ≈ 5 swaps / ISO week. */
export const SUBSCRIBER_SWAP_COST = 10_000;

/** English fallback; UI should prefer t("billing.paywallCopy"). */
export const PAYWALL_COPY = en.billing.paywallCopy;

export type BillingPlan = "trial" | "subscriber" | "vip" | "free";
export type PaidTier = "subscriber" | "vip";

export type BillingState = {
  plan: BillingPlan;
  trialEndsAt: string | null;
  subscriptionUntil: string | null;
  priceUsd: number;
  /** Credit balance; null means unlimited (VIP). */
  credits: number | null;
  unlimitedCredits: boolean;
  unlimitedMatching: boolean;
  canLike: true;
  canSendMessage: true;
  /** Weekly curated four — all tiers. */
  canBrowseWeeklyFour: boolean;
  canBrowseDeck: boolean;
  canSeeWhoLikedYou: boolean;
  canUseExtraReveals: boolean;
  /** Free: pay 10k to rebuild the four once the week is spent / on demand. */
  canWeeklySwitch: boolean;
  /** Subscriber: credit-priced refreshes (50 000 / week ≈ 5 swaps). Not a daily cap. */
  canDailySwap: boolean;
  /** VIP: refresh anytime, no deduct. */
  canUnlimitedSwap: boolean;
  /** ISO day YYYY-MM-DD of last paid/daily swap, if any. */
  lastSwapDay: string | null;
  /**
   * Hearts this plan gets each ISO week; null = unlimited (VIP).
   * Hearts are NOT credits: credits buy new people, hearts spend on a person.
   * Balance spent/remaining lives in matching/heartWeekStore — this is the
   * entitlement only. (Masco 2026-08-29.)
   */
  heartBudget: number | null;
  reason?: string;
  copy: string;
};

const LOCAL_KEY = "un8.billing.v1";
const DAY_MS = 24 * 60 * 60 * 1000;

type LocalRecord = {
  userId: string;
  /** Anchored account / trial start — end date is start + TRIAL_DAYS when on paid trial. */
  trialStartedAt?: string;
  trialEndsAt: string;
  subscriptionUntil: string | null;
  /** Active paid product once subscribed (or during paid trial). */
  paidTier?: PaidTier | null;
  credits?: number;
  /** ISO week key of last free weekly credit recharge. */
  lastCreditWeek?: string;
  /** YYYY-MM-DD of last deck swap (free switch or subscriber daily). */
  lastSwapDay?: string | null;
};

/** Stable end date from anchored start (exactly TRIAL_DAYS). */
export function trialEndFromStart(startIso: string): string {
  const start = Date.parse(startIso);
  const base = Number.isFinite(start) ? start : Date.now();
  return new Date(base + TRIAL_DAYS * DAY_MS).toISOString();
}

function utcDayKey(d = new Date()): string {
  return d.toISOString().slice(0, 10);
}

function emptyBilling(): BillingState {
  return {
    plan: "free",
    trialEndsAt: null,
    subscriptionUntil: null,
    priceUsd: SUBSCRIPTION_PRICE_USD,
    credits: 0,
    unlimitedCredits: false,
    unlimitedMatching: false,
    canLike: true,
    canSendMessage: true,
    canBrowseWeeklyFour: false,
    canBrowseDeck: false,
    canSeeWhoLikedYou: false,
    canUseExtraReveals: false,
    canWeeklySwitch: false,
    canDailySwap: false,
    canUnlimitedSwap: false,
    lastSwapDay: null,
    heartBudget: 0,
    reason: "Sign in to use matching.",
    copy: PAYWALL_COPY,
  };
}

function applyWeeklyRecharge(rec: LocalRecord, now = Date.now()): LocalRecord {
  const wk = weekKey(new Date(now));
  if (rec.paidTier === "vip") return rec;
  if (rec.credits == null) rec.credits = FREE_START_CREDITS;
  if (rec.lastCreditWeek !== wk) {
    const subActive =
      rec.paidTier === "subscriber" &&
      ((rec.subscriptionUntil && Date.parse(rec.subscriptionUntil) > now) ||
        Date.parse(rec.trialEndsAt) > now);
    if (subActive) {
      rec.credits = SUBSCRIBER_START_CREDITS;
    } else if (!rec.paidTier) {
      rec.credits = (rec.credits ?? 0) + FREE_WEEKLY_RECHARGE;
      // First week already has start balance; avoid double-counting on brand-new records
      // by only adding when lastCreditWeek was previously set.
      if (!rec.lastCreditWeek) {
        rec.credits = FREE_START_CREDITS;
      }
    }
    rec.lastCreditWeek = wk;
  }
  return rec;
}

function resolveLocal(rec: LocalRecord, now = Date.now()): BillingState {
  const patched = applyWeeklyRecharge({ ...rec }, now);
  // Persist recharge side-effects when credits/week changed.
  if (
    patched.credits !== rec.credits ||
    patched.lastCreditWeek !== rec.lastCreditWeek
  ) {
    const map = readMap();
    map[patched.userId] = patched;
    writeMap(map);
  }

  const subUntil = patched.subscriptionUntil
    ? Date.parse(patched.subscriptionUntil)
    : 0;
  const trialLive = Date.parse(patched.trialEndsAt) > now;
  const paidLive = subUntil > now;
  const tier = patched.paidTier ?? null;

  if (tier === "vip" && (paidLive || trialLive)) {
    return {
      plan: paidLive ? "vip" : "trial",
      trialEndsAt: patched.trialEndsAt,
      subscriptionUntil: patched.subscriptionUntil,
      priceUsd: VIP_PRICE_USD,
      credits: null,
      unlimitedCredits: true,
      unlimitedMatching: true,
      canLike: true,
      canSendMessage: true,
      canBrowseWeeklyFour: true,
      canBrowseDeck: true,
      canSeeWhoLikedYou: true,
      canUseExtraReveals: true,
      canWeeklySwitch: true,
      canDailySwap: true,
      canUnlimitedSwap: true,
      lastSwapDay: patched.lastSwapDay ?? null,
      heartBudget: heartsPerWeek("vip"),
      copy: PAYWALL_COPY,
    };
  }

  if (tier === "subscriber" && (paidLive || trialLive)) {
    const credits = patched.credits ?? SUBSCRIBER_START_CREDITS;
    return {
      plan: paidLive ? "subscriber" : "trial",
      trialEndsAt: patched.trialEndsAt,
      subscriptionUntil: patched.subscriptionUntil,
      priceUsd: SUBSCRIPTION_PRICE_USD,
      credits,
      unlimitedCredits: false,
      unlimitedMatching: true,
      canLike: true,
      canSendMessage: true,
      canBrowseWeeklyFour: true,
      canBrowseDeck: true,
      canSeeWhoLikedYou: true,
      canUseExtraReveals: true,
      canWeeklySwitch: false,
      canDailySwap: true,
      canUnlimitedSwap: false,
      lastSwapDay: patched.lastSwapDay ?? null,
      heartBudget: heartsPerWeek("subscriber"),
      copy: PAYWALL_COPY,
    };
  }

  // Legacy: old 30-day unlimited trial rows without paidTier → treat remaining
  // window as subscriber trial once, then free.
  if (!tier && trialLive && !paidLive) {
    const credits = patched.credits ?? SUBSCRIBER_START_CREDITS;
    return {
      plan: "trial",
      trialEndsAt: patched.trialEndsAt,
      subscriptionUntil: patched.subscriptionUntil,
      priceUsd: SUBSCRIPTION_PRICE_USD,
      credits,
      unlimitedCredits: false,
      unlimitedMatching: true,
      canLike: true,
      canSendMessage: true,
      canBrowseWeeklyFour: true,
      canBrowseDeck: true,
      canSeeWhoLikedYou: false,
      canUseExtraReveals: false,
      canWeeklySwitch: true,
      canDailySwap: true,
      canUnlimitedSwap: false,
      lastSwapDay: patched.lastSwapDay ?? null,
      heartBudget: heartsPerWeek("trial"),
      copy: PAYWALL_COPY,
    };
  }

  const credits = patched.credits ?? FREE_START_CREDITS;
  return {
    plan: "free",
    trialEndsAt: patched.trialEndsAt,
    subscriptionUntil: patched.subscriptionUntil,
    priceUsd: SUBSCRIPTION_PRICE_USD,
    credits,
    unlimitedCredits: false,
    unlimitedMatching: false,
    canLike: true,
    canSendMessage: true,
    canBrowseWeeklyFour: true,
    canBrowseDeck: true,
    canSeeWhoLikedYou: false,
    canUseExtraReveals: false,
    canWeeklySwitch: credits >= WEEKLY_SWITCH_COST,
    canDailySwap: false,
    canUnlimitedSwap: false,
    lastSwapDay: patched.lastSwapDay ?? null,
    heartBudget: heartsPerWeek("free"),
    reason: PAYWALL_COPY,
    copy: PAYWALL_COPY,
  };
}

function readMap(): Record<string, LocalRecord> {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, LocalRecord>;
  } catch {
    return {};
  }
}

function writeMap(map: Record<string, LocalRecord>) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(map));
}

function ensureLocal(userId: string): LocalRecord {
  const map = readMap();
  let rec = map[userId];
  if (!rec) {
    const startedAt = new Date().toISOString();
    rec = {
      userId,
      trialStartedAt: startedAt,
      // Free has no paid trial window — end equals start so plan resolves to free.
      trialEndsAt: startedAt,
      subscriptionUntil: null,
      paidTier: null,
      credits: FREE_START_CREDITS,
      lastCreditWeek: weekKey(),
      lastSwapDay: null,
    };
    map[userId] = rec;
    writeMap(map);
    return rec;
  }

  if (!rec.trialStartedAt) {
    const endMs = Date.parse(rec.trialEndsAt);
    const now = Date.now();
    let startMs = Number.isFinite(endMs) ? endMs - TRIAL_DAYS * DAY_MS : now;
    if (startMs > now) startMs = now;
    rec.trialStartedAt = new Date(startMs).toISOString();
    // Keep existing trialEndsAt when it is still in the future (legacy 30-day rows).
    if (!Number.isFinite(endMs) || endMs <= now) {
      rec.trialEndsAt = rec.trialEndsAt || rec.trialStartedAt;
    }
  }

  if (rec.credits == null && !rec.paidTier) {
    // Legacy trial still live → leave credits unset so resolveLocal keeps trial entitlements.
    // Post-trial free → seed starting balance.
    if (!(Date.parse(rec.trialEndsAt) > Date.now())) {
      rec.credits = FREE_START_CREDITS;
    }
  }
  if (!rec.lastCreditWeek) {
    rec.lastCreditWeek = weekKey();
  }

  // Paid tiers keep a precise TRIAL_DAYS window from trialStartedAt.
  if (rec.paidTier && rec.trialStartedAt && !rec.subscriptionUntil) {
    const expectedEnd = trialEndFromStart(rec.trialStartedAt);
    if (rec.trialEndsAt !== expectedEnd) rec.trialEndsAt = expectedEnd;
  }

  map[userId] = rec;
  writeMap(map);
  return rec;
}

type Listener = () => void;
const listeners = new Set<Listener>();

let cached: BillingState = emptyBilling();
let lastUserId: string | null = null;

function emit() {
  for (const l of listeners) l();
}

function fromServerPayload(b: Partial<BillingState> & { plan?: BillingPlan }): BillingState {
  const plan = b.plan || "free";
  const isSub = plan === "subscriber" || plan === "vip";
  const isVip = plan === "vip";
  const weeklyFour =
    b.canBrowseWeeklyFour !== undefined
      ? Boolean(b.canBrowseWeeklyFour)
      : plan === "trial" || plan === "free" || isSub
        ? true
        : Boolean(b.canBrowseDeck);
  const unlimitedCredits =
    b.unlimitedCredits !== undefined ? Boolean(b.unlimitedCredits) : isVip;
  return {
    plan,
    trialEndsAt: b.trialEndsAt ?? null,
    subscriptionUntil: b.subscriptionUntil ?? null,
    priceUsd:
      Number(b.priceUsd) || (isVip ? VIP_PRICE_USD : SUBSCRIPTION_PRICE_USD),
    credits: unlimitedCredits ? null : b.credits ?? FREE_START_CREDITS,
    unlimitedCredits,
    unlimitedMatching: Boolean(b.unlimitedMatching) || isSub,
    canLike: true,
    canSendMessage: true,
    canBrowseWeeklyFour: weeklyFour,
    canBrowseDeck: weeklyFour,
    canSeeWhoLikedYou:
      b.canSeeWhoLikedYou !== undefined ? Boolean(b.canSeeWhoLikedYou) : isSub,
    canUseExtraReveals:
      b.canUseExtraReveals !== undefined ? Boolean(b.canUseExtraReveals) : isSub,
    canWeeklySwitch:
      b.canWeeklySwitch !== undefined
        ? Boolean(b.canWeeklySwitch)
        : plan === "free" || plan === "trial" || isVip,
    canDailySwap:
      b.canDailySwap !== undefined
        ? Boolean(b.canDailySwap)
        : plan === "subscriber" || plan === "trial" || isVip,
    canUnlimitedSwap:
      b.canUnlimitedSwap !== undefined ? Boolean(b.canUnlimitedSwap) : isVip,
    lastSwapDay: b.lastSwapDay ?? null,
    // Server may not send it yet; derive from the plan it did send.
    heartBudget:
      b.heartBudget !== undefined ? b.heartBudget : heartsPerWeek(plan),
    reason: b.reason,
    copy: b.copy || PAYWALL_COPY,
  };
}

function persistAndResolve(userId: string, mutate: (rec: LocalRecord) => void): BillingState {
  const map = readMap();
  const rec = ensureLocal(userId);
  mutate(rec);
  map[userId] = rec;
  writeMap(map);
  cached = resolveLocal(rec);
  emit();
  return cached;
}

export const billingStore = {
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },

  get(): BillingState {
    return cached;
  },

  hydrateLocal(userId: string | null): BillingState {
    if (!userId) {
      if (lastUserId) return this.hydrateLocal(lastUserId);
      cached = emptyBilling();
      emit();
      return cached;
    }
    lastUserId = userId;
    cached = resolveLocal(ensureLocal(userId));
    emit();
    return cached;
  },

  clearForLogout() {
    lastUserId = null;
    cached = emptyBilling();
    emit();
  },

  async refresh(userId: string | null): Promise<BillingState> {
    if (!userId) {
      if (lastUserId) return this.hydrateLocal(lastUserId);
      cached = emptyBilling();
      emit();
      return cached;
    }
    lastUserId = userId;
    if (isServerMode()) {
      const res = await apiJson<{ billing?: BillingState }>("/api/billing", "GET");
      if (res.ok && res.data.billing) {
        cached = fromServerPayload(res.data.billing);
        emit();
        return cached;
      }
    }
    return this.hydrateLocal(userId);
  },

  /**
   * Apply a verified Play Billing entitlement (Digital Goods handshake).
   * Trial window starts now; Play Console owns the 14-day free trial charge timing.
   */
  applyPlayPurchase(userId: string, tier: PaidTier): BillingState {
    return persistAndResolve(userId, (rec) => {
      const now = Date.now();
      rec.paidTier = tier;
      rec.trialStartedAt = new Date(now).toISOString();
      rec.trialEndsAt = trialEndFromStart(rec.trialStartedAt);
      // Entitlement active through trial + first billed month until RTDN lands.
      rec.subscriptionUntil = new Date(now + (TRIAL_DAYS + 30) * DAY_MS).toISOString();
      if (tier === "subscriber") {
        rec.credits = SUBSCRIBER_START_CREDITS;
        rec.lastCreditWeek = weekKey();
      } else {
        rec.credits = undefined;
      }
    });
  },

  /**
   * Closed-test: start / extend Subscriber ($4.99). First call seeds 50k credits
   * and a 14-day free trial window; also sets subscriptionUntil for demo month.
   */
  async activateSubscription(
    userId: string | null
  ): Promise<{ ok: true } | { ok: false; error: string }> {
    if (!userId) return { ok: false, error: "Sign in first." };
    if (isServerMode()) {
      const res = await apiJson<{ billing?: BillingState }>(
        "/api/billing/subscribe",
        "POST",
        { tier: "subscriber" }
      );
      if (!res.ok) return { ok: false, error: res.error };
      if (res.data.billing) {
        cached = fromServerPayload(res.data.billing);
        emit();
      } else {
        await this.refresh(userId);
      }
      return { ok: true };
    }
    persistAndResolve(userId, (rec) => {
      const now = Date.now();
      const start =
        rec.subscriptionUntil && Date.parse(rec.subscriptionUntil) > now
          ? Date.parse(rec.subscriptionUntil)
          : now;
      rec.paidTier = "subscriber";
      rec.credits = SUBSCRIBER_START_CREDITS;
      rec.lastCreditWeek = weekKey();
      rec.trialStartedAt = new Date(now).toISOString();
      rec.trialEndsAt = trialEndFromStart(rec.trialStartedAt);
      rec.subscriptionUntil = new Date(start + 30 * DAY_MS).toISOString();
    });
    return { ok: true };
  },

  /** Closed-test: VIP club ($9.99) — unlimited credits + swaps. */
  async activateVip(
    userId: string | null
  ): Promise<{ ok: true } | { ok: false; error: string }> {
    if (!userId) return { ok: false, error: "Sign in first." };
    persistAndResolve(userId, (rec) => {
      const now = Date.now();
      const start =
        rec.subscriptionUntil && Date.parse(rec.subscriptionUntil) > now
          ? Date.parse(rec.subscriptionUntil)
          : now;
      rec.paidTier = "vip";
      rec.credits = undefined;
      rec.trialStartedAt = new Date(now).toISOString();
      rec.trialEndsAt = trialEndFromStart(rec.trialStartedAt);
      rec.subscriptionUntil = new Date(start + 30 * DAY_MS).toISOString();
    });
    return { ok: true };
  },

  /**
   * Attempt a deck swap / weekly switch.
   * Free: deduct 10 000. Subscriber: deduct 10 000 per refresh (5 / week at 50 000).
   * VIP: no deduct, always ok.
   */
  tryDeckSwap(
    userId: string | null
  ): { ok: true; credits: number | null } | { ok: false; error: string } {
    if (!userId) return { ok: false, error: "Sign in first." };
    const state = this.hydrateLocal(userId);
    const today = utcDayKey();

    if (state.unlimitedCredits || state.canUnlimitedSwap) {
      persistAndResolve(userId, (rec) => {
        rec.lastSwapDay = today;
      });
      return { ok: true, credits: null };
    }

    if (state.canDailySwap) {
      if ((state.credits ?? 0) < SUBSCRIBER_SWAP_COST) {
        return { ok: false, error: "Not enough credits for a swap. Each refresh costs 10 000." };
      }
      persistAndResolve(userId, (rec) => {
        rec.credits = Math.max(0, (rec.credits ?? 0) - SUBSCRIBER_SWAP_COST);
        rec.lastSwapDay = today;
      });
      return { ok: true, credits: billingStore.get().credits };
    }

    // Free weekly switch
    if ((state.credits ?? 0) < WEEKLY_SWITCH_COST) {
      return { ok: false, error: "Not enough credits. Weekly switch costs 10 000." };
    }
    persistAndResolve(userId, (rec) => {
      rec.credits = Math.max(0, (rec.credits ?? 0) - WEEKLY_SWITCH_COST);
      rec.lastSwapDay = today;
    });
    return { ok: true, credits: billingStore.get().credits };
  },
};
